/**
 * Driver dashboard non-active surfaces (iter-73 decomposition slice 4).
 *
 * Four small presentational components extracted verbatim from
 * Driver.jsx. Each is byte-equivalent to the inline JSX it replaces
 * — no logic, no derived state, no handlers moved.
 *
 *   1. `EarningsTodayLink`   — Link → /driver/earnings (always rendered)
 *   2. `WaitingForOffersCard`— "WAITING FOR OFFERS" idle state
 *   3. `OfflineCard`         — "YOU'RE OFFLINE" empty state
 *   4. `RecentTripsList`     — last 8 completed trips, with empty fallback
 *
 * Kept in one file because each is < 25 lines and they always live
 * next to each other in the parent's render tree. If any of them
 * grow non-trivially in future, split them — they're independently
 * exportable today.
 *
 * Rollback: delete this file, paste each block back where it was
 * in Driver.jsx, remove the imports. No coupling to slices 1–3.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Power } from "lucide-react";


export function EarningsTodayLink() {
  return (
    <Link
      to="/driver/earnings"
      className="bg-white border border-border p-3 flex items-center justify-between hover:bg-secondary transition-colors"
      data-testid="driver-earnings-link"
    >
      <div>
        <div className="label-mini text-muted-foreground">EARNINGS · TODAY</div>
        <div className="text-xs text-muted-foreground mt-1">View gross, daily firm fee and net</div>
      </div>
      <div className="text-xs font-bold uppercase tracking-wider">Open →</div>
    </Link>
  );
}


export function MembershipLink() {
  return (
    <Link
      to="/driver/subscription"
      className="bg-white border border-border p-3 flex items-center justify-between hover:bg-secondary transition-colors"
      data-testid="driver-membership-link"
    >
      <div>
        <div className="label-mini text-muted-foreground">MEMBERSHIP</div>
        <div className="text-xs text-muted-foreground mt-1">Manage your access subscription</div>
      </div>
      <div className="text-xs font-bold uppercase tracking-wider">Open →</div>
    </Link>
  );
}


export function WaitingForOffersCard() {
  return (
    <div className="border border-dashed border-border p-8 text-center" data-testid="waiting-state">
      <div className="w-12 h-12 mx-auto rounded-full bg-secondary flex items-center justify-center mb-3">
        <Power className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="label-mini text-muted-foreground">WAITING FOR OFFERS</div>
      <div className="text-sm text-muted-foreground mt-2">You're online. We'll ping you when a rider needs you.</div>
    </div>
  );
}


/**
 * OfflineCard — "YOU'RE OFFLINE" idle empty-state.
 *
 * The parent gates this on `!isOnline && !pendingOffer && !activeRide`
 * to prevent the message appearing under a tappable Accept button (root
 * cause of the original "no way to accept" report). That gating logic
 * stays in the parent — this component is unconditional render once
 * the parent decides to mount it.
 */
export function OfflineCard() {
  return (
    <div className="border border-dashed border-border p-8 text-center" data-testid="offline-state">
      <div className="w-12 h-12 mx-auto rounded-full bg-secondary flex items-center justify-center mb-3">
        <Power className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="label-mini text-muted-foreground">YOU'RE OFFLINE</div>
      <div className="text-sm text-muted-foreground mt-2">Flip the switch to start receiving rides.</div>
    </div>
  );
}


/**
 * RecentTripsList — header row + last 8 completed trips with empty
 * fallback. Parent owns the `completedRides` array (loaded once at
 * mount via `/driver/me`); we receive it as a prop and render. No
 * pagination logic here — slice(0, 8) matches the original.
 */
export function RecentTripsList({ completedRides }) {
  return (
    <div className="border-t border-border pt-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="label-mini text-muted-foreground">RECENT TRIPS</div>
        <div className="label-mini text-muted-foreground">{completedRides.length} TOTAL</div>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="trip-history">
        {completedRides.slice(0, 8).map((r) => (
          <div key={r.id} className="border border-border p-3 flex items-center justify-between" data-testid={`driver-trip-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{r.pickup_address} → {r.dropoff_address}</div>
              <div className="label-mini text-muted-foreground mt-1">{r.passenger_name}</div>
            </div>
            <div className="font-mono-tab font-bold text-sm">£{r.fare}</div>
          </div>
        ))}
        {completedRides.length === 0 && (
          <div className="text-sm text-muted-foreground">No completed trips yet.</div>
        )}
      </div>
    </div>
  );
}
